/**
 * A published version of the customer-facing Terms of Service.
 *
 * `mode` decides what studio does once `effectiveAt` has passed: "soft" shows a
 * dismissible notice, "blocking" stops the org until somebody acknowledges. It
 * lives on the row rather than in studio's code so a rollout can be softened or
 * hardened without shipping a release — useful if a blocking prompt turns out to
 * be landing badly on an orchestra mid-season.
 *
 * @param tosVersionJson
 */
function validator(tosVersionJson: any) {
  TosVersion_Entity.requiredFields.forEach((element) => {
    if (!Object.keys(tosVersionJson).includes(element))
      console.log("Field ${element} is null or undefined");
  });
}

export type TosMode = "soft" | "blocking";

/** Which legal document a version belongs to. */
export type TosKind = "terms" | "privacy" | "payroll" | "musician";

/**
 * Who a document applies to.
 *
 * The audience selects the population, not just the scope: "musicians" means every
 * local420 musician and no organization, so a document published to them never
 * reaches Studio and a customer contract never reaches the musician apps.
 * "organizations" reads the audience list.
 */
export type TosAudience = "all" | "organizations" | "musicians";

export default class TosVersion_Entity {
  static requiredFields = ["id", "version", "url"];

  constructor(tosVersionJson: any) {
    try {
      validator(tosVersionJson);

      this.id = tosVersionJson.id;
      this.kind = tosVersionJson.kind;
      this.version = tosVersionJson.version;
      this.url = tosVersionJson.url;
      this.content = tosVersionJson.content;
      this.summary = tosVersionJson.summary;
      this.publishedAt = tosVersionJson.publishedAt;
      this.effectiveAt = tosVersionJson.effectiveAt;
      this.mode = tosVersionJson.mode;
      this.requiresAcceptance = tosVersionJson.requiresAcceptance;
      this.audience = tosVersionJson.audience;
      this.organizationIDs = tosVersionJson.organizationIDs;
      this.active = tosVersionJson.active;
      this.createdAt = tosVersionJson.createdAt;
      this.updatedAt = tosVersionJson.updatedAt;
    } catch (e) {
      throw new Error(
        "Failed to create new instance of ${ TosVersion_Entity.name }: ${e}"
      );
    }
  }

  id: number;
  kind?: TosKind;
  /** Matches the "Last updated" date on the published document. */
  version: string;
  url: string;
  /**
   * The document text as Markdown, snapshotted at publish time. Frozen once any
   * organization has acknowledged the version.
   */
  content?: string;
  summary?: string;
  publishedAt?: string;
  /** When this version starts binding customers. */
  effectiveAt?: string;
  mode?: TosMode;
  /** False = notice only, nothing to accept (the Privacy Policy). */
  requiresAcceptance?: boolean;
  audience?: TosAudience;
  /** Audience list, present when audience is "organizations". */
  organizationIDs?: number[];
  active?: boolean;
  createdAt: string;
  updatedAt: string;
}
